/**
 * Assets module — Excel export.
 *
 * Was a hand-rolled HTML-table-as-.xls trick (duplicated across Inventory/Assets/Procurement/
 * Requisitions). Consolidated 2026-07-13 to the shared, letterheaded xlsx exporter — see
 * src/features/inventory/utils/export.ts for the identical rationale. Same function name/
 * signature, now async, downloads .xlsx instead of .xls.
 *
 * Also re-exports the row-builder helpers (ASSET_EXPORT_HEADERS/assetToRow,
 * DISPOSAL_EXPORT_HEADERS/disposalToRow, MAINTENANCE_EXPORT_HEADERS/maintenanceToRow) used by
 * AssetListPage, AssetReportsPage, and AssetDisposalPage to build their export rows — column
 * sets mirror each page's on-screen table.
 */
import { exportToXlsx, type ExportRow } from "#/lib/xlsxExport";
import { fmtDate } from "../components/AssetUI";
import type { Asset, AssetSummary, AssetDisposal, AssetMaintenance } from "../types";

export type { ExportRow };

export function downloadXls(filename: string, title: string, headers: string[], rows: ExportRow[]): Promise<void> {
  return exportToXlsx({ filename, title, headers, rows });
}

// ─── Asset register ────────────────────────────────────────────────────────────

export const ASSET_EXPORT_HEADERS = [
  "Asset Number", "Name", "Category", "Location", "Status", "Condition",
  "Assigned To", "Purchase Cost (KES)", "Current Value (KES)",
];

export function assetToRow(a: Asset | AssetSummary): ExportRow {
  return [
    a.assetNumber,
    a.name,
    a.categoryName,
    a.locationName ?? "—",
    a.status,
    a.condition,
    a.assignedEmployeeName ?? "—",
    a.purchaseCost ?? null,
    a.currentValue ?? null,
  ];
}

// ─── Disposals ──────────────────────────────────────────────────────────────────

export const DISPOSAL_EXPORT_HEADERS = [
  "Asset Number", "Asset Name", "Reason", "Method",
  "Requested By", "Requested", "Approved By", "Value (KES)", "Status",
];

export function disposalToRow(d: AssetDisposal): ExportRow {
  return [
    d.assetNumber ?? "—",
    d.assetName,
    d.reason,
    d.disposalMethod?.replace(/_/g, " ") ?? "—",
    d.requestedBy ?? "—",
    fmtDate(d.requestedAt),
    d.approvedBy ?? "—",
    d.disposalValue ?? null,
    d.status,
  ];
}

// ─── Maintenance ────────────────────────────────────────────────────────────────

export const MAINTENANCE_EXPORT_HEADERS = [
  "Asset Number", "Asset Name", "Type", "Status",
  "Scheduled", "Completed", "Technician", "Vendor", "Cost (KES)",
];

export function maintenanceToRow(m: AssetMaintenance): ExportRow {
  return [
    m.assetNumber,
    m.assetName ?? "—",
    m.maintenanceType.replace(/_/g, " "),
    m.status,
    fmtDate(m.scheduledDate),
    fmtDate(m.completedDate),
    m.technician ?? "—",
    m.vendor ?? "—",
    m.cost ?? null,
  ];
}
